import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FaBan,
  FaCheckCircle,
  FaClock,
  FaEnvelope,
  FaExclamationCircle,
  FaExternalLinkAlt,
  FaLock,
  FaRedo,
  FaShieldAlt,
  FaTimes,
} from "react-icons/fa";

import "./UnSubscribeStyles.css";

type UnSubscribeProps = {
  isOpen: boolean;
  onClose: () => void;
  userEmail?: string | null;
  userId?: string | null;
  subscriptionIds?: string[];
  onPortalOpened?: () => void;
  onCancelled?: () => void;
};

type SubscriptionStatusResponse = {
  success?: boolean;
  has_subscription?: boolean;
  customer_id?: string | null;
  subscription_id?: string | null;
  status?: string | null;
  auto_renewal_status?: string | null;
  current_period_ends_at?: number | null;
  gives_access?: boolean;
  product_keys?: string[];
  entitlement_keys?: string[];
  message?: string;
  error?: string | null;
};

type CancelResponse = {
  success?: boolean;
  cancelled?: boolean;
  already_cancelled?: boolean;
  customer_id?: string | null;
  subscription_id?: string | null;
  status?: string | null;
  auto_renewal_status?: string | null;
  current_period_ends_at?: number | null;
  message?: string;
  error?: string | null;
  revenuecat_error?: string | null;
};

type PortalResponse = {
  success?: boolean;
  message?: string;
  management_url?: string | null;
  portal_url?: string | null;
  customer_id?: string | null;
  subscription_id?: string | null;
  status?: string | null;
  auto_renewal_status?: string | null;
  current_period_ends_at?: number | null;
  error?: string | null;
};

const API_BASE_URL = (
  process.env.REACT_APP_BACKEND_API_URL ||
  process.env.REACT_APP_API_BASE_URL ||
  "https://www.api.dearlanguage.com"
).replace(/\/+$/, "");

const normalizeEmail = (email?: string | null): string => {
  return String(email || "")
    .trim()
    .toLowerCase();
};

const normalizeId = (value?: string | null): string => {
  return String(value || "").trim();
};

const cleanString = (value?: string | null): string => {
  return String(value || "").trim();
};

const getReadableError = (error: unknown): string => {
  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }

  if (typeof error === "string" && error.trim()) {
    return error.trim();
  }

  return "Die Aktion konnte nicht abgeschlossen werden. Bitte versuchen Sie es erneut.";
};

const isValidPortalUrl = (value: string): boolean => {
  return /^https?:\/\//i.test(value.trim());
};

const formatTimestampDate = (value?: number | null): string => {
  if (!value) {
    return "—";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "—";
  }

  return new Intl.DateTimeFormat("de-DE", {
    dateStyle: "medium",
  }).format(date);
};

const formatStatus = (value?: string | null): string => {
  const cleanValue = cleanString(value).toLowerCase();

  if (!cleanValue) {
    return "—";
  }

  if (cleanValue === "active") {
    return "Aktiv";
  }

  if (cleanValue === "expired") {
    return "Abgelaufen";
  }

  if (cleanValue === "cancelled" || cleanValue === "canceled") {
    return "Gekündigt";
  }

  return cleanString(value);
};

const isCancelledAutoRenewal = (value?: string | null): boolean => {
  const cleanValue = cleanString(value).toLowerCase();

  return (
    cleanValue.includes("will_not_renew") ||
    cleanValue.includes("cancel") ||
    cleanValue.includes("canceled") ||
    cleanValue.includes("cancelled")
  );
};

const formatAutoRenewal = (value?: string | null): string => {
  if (isCancelledAutoRenewal(value)) {
    return "Gekündigt";
  }

  const cleanValue = cleanString(value).toLowerCase();

  if (!cleanValue) {
    return "—";
  }

  if (cleanValue === "will_renew") {
    return "Aktiv";
  }

  return cleanString(value);
};

const UnSubscribe: React.FC<UnSubscribeProps> = ({
  isOpen,
  onClose,
  userEmail,
  userId,
  subscriptionIds = [],
  onPortalOpened,
  onCancelled,
}) => {
  const [statusLoading, setStatusLoading] = useState<boolean>(false);
  const [cancelLoading, setCancelLoading] = useState<boolean>(false);
  const [portalLoading, setPortalLoading] = useState<boolean>(false);
  const [errorText, setErrorText] = useState<string>("");
  const [successText, setSuccessText] = useState<string>("");
  const [statusData, setStatusData] =
    useState<SubscriptionStatusResponse | null>(null);
  const [cancelData, setCancelData] = useState<CancelResponse | null>(null);

  const cleanEmail = useMemo(() => normalizeEmail(userEmail), [userEmail]);
  const cleanUserId = useMemo(() => normalizeId(userId), [userId]);

  const firstSubscriptionId = useMemo(() => {
    return (
      subscriptionIds
        .map((item) => String(item || "").trim())
        .find(Boolean) || ""
    );
  }, [subscriptionIds]);

  const resolvedSubscriptionId =
    cleanString(cancelData?.subscription_id) ||
    cleanString(statusData?.subscription_id) ||
    firstSubscriptionId;

  const resolvedCustomerId =
    cleanString(cancelData?.customer_id) ||
    cleanString(statusData?.customer_id);

  const resolvedStatus =
    cleanString(cancelData?.status) || cleanString(statusData?.status);

  const resolvedAutoRenewalStatus =
    cleanString(cancelData?.auto_renewal_status) ||
    cleanString(statusData?.auto_renewal_status);

  const resolvedPeriodEnd =
    cancelData?.current_period_ends_at || statusData?.current_period_ends_at;

  const isAlreadyCancelled = Boolean(
    cancelData?.already_cancelled ||
      isCancelledAutoRenewal(resolvedAutoRenewalStatus),
  );

  const hasSubscription = Boolean(
    statusData?.has_subscription ||
      resolvedSubscriptionId ||
      cancelData?.success ||
      cancelData?.cancelled ||
      cancelData?.already_cancelled,
  );

  const canRunActions = Boolean(cleanEmail || cleanUserId);

  const notifyAccessChanged = useCallback((): void => {
    window.dispatchEvent(new CustomEvent("dear-language-profile-refresh"));
    window.dispatchEvent(new CustomEvent("dear-language-books-access-refresh"));
    window.dispatchEvent(new CustomEvent("dear-language-course-access-refresh"));
    window.dispatchEvent(new CustomEvent("dear-language-premium-updated"));
  }, []);

  const buildQuery = useCallback((): URLSearchParams => {
    const query = new URLSearchParams();

    if (cleanEmail) {
      query.set("email", cleanEmail);
    }

    if (cleanUserId) {
      query.set("app_user_id", cleanUserId);
    }

    if (resolvedSubscriptionId) {
      query.set("subscription_id", resolvedSubscriptionId);
    }

    query.set("force_refresh", "1");
    query.set("t", String(Date.now()));

    return query;
  }, [cleanEmail, cleanUserId, resolvedSubscriptionId]);

  const resetMessages = useCallback((): void => {
    setErrorText("");
    setSuccessText("");
  }, []);

  const loadStatus = useCallback(async (): Promise<void> => {
    resetMessages();

    if (!canRunActions) {
      setErrorText("E-Mail-Adresse oder Benutzer-ID fehlt.");
      return;
    }

    setStatusLoading(true);

    try {
      const query = buildQuery();

      const response = await fetch(
        `${API_BASE_URL}/api/revenuecat/unsubscribe/status?${query.toString()}`,
        {
          method: "GET",
          cache: "no-store",
          headers: {
            Accept: "application/json",
            "Cache-Control": "no-cache",
            Pragma: "no-cache",
          },
        },
      );

      const data = (await response
        .json()
        .catch(() => ({}))) as SubscriptionStatusResponse;

      if (!response.ok) {
        throw new Error(
          data?.message ||
            data?.error ||
            `Abo-Status konnte nicht geladen werden: ${response.status}`,
        );
      }

      setStatusData(data);

      if (!data.has_subscription) {
        setSuccessText(data.message || "Kein aktives Website-Premium-Abo gefunden.");
        return;
      }

      setSuccessText(data.message || "Website-Premium-Abo gefunden.");
    } catch (error) {
      setErrorText(getReadableError(error));
    } finally {
      setStatusLoading(false);
    }
  }, [buildQuery, canRunActions, resetMessages]);

  const handleOpenPortal = async (): Promise<void> => {
    resetMessages();

    if (!canRunActions) {
      setErrorText("E-Mail-Adresse oder Benutzer-ID fehlt.");
      return;
    }

    setPortalLoading(true);

    try {
      const query = buildQuery();

      const response = await fetch(
        `${API_BASE_URL}/api/revenuecat/customer-portal?${query.toString()}`,
        {
          method: "GET",
          cache: "no-store",
          headers: {
            Accept: "application/json",
            "Cache-Control": "no-cache",
            Pragma: "no-cache",
          },
        },
      );

      const data = (await response.json().catch(() => ({}))) as PortalResponse;

      if (!response.ok) {
        throw new Error(
          data?.message ||
            data?.error ||
            `Kundenportal konnte nicht geöffnet werden: ${response.status}`,
        );
      }

      const portalUrl = cleanString(data.management_url || data.portal_url);

      if (!portalUrl || !isValidPortalUrl(portalUrl)) {
        throw new Error(
          "RevenueCat hat keine gültige Kundenportal-URL zurückgegeben.",
        );
      }

      setSuccessText("Kundenportal wird geöffnet...");

      onPortalOpened?.();
      notifyAccessChanged();

      const openedWindow = window.open(
        portalUrl,
        "_blank",
        "noopener,noreferrer",
      );

      if (!openedWindow) {
        window.location.assign(portalUrl);
      }
    } catch (error) {
      setErrorText(getReadableError(error));
    } finally {
      setPortalLoading(false);
    }
  };

  const handleCancelSubscription = async (): Promise<void> => {
    resetMessages();

    if (!canRunActions) {
      setErrorText("E-Mail-Adresse oder Benutzer-ID fehlt.");
      return;
    }

    setCancelLoading(true);

    try {
      const payload: Record<string, string> = {};

      if (cleanEmail) {
        payload.email = cleanEmail;
      }

      if (cleanUserId) {
        payload.app_user_id = cleanUserId;
      }

      if (resolvedSubscriptionId) {
        payload.subscription_id = resolvedSubscriptionId;
      }

      const response = await fetch(`${API_BASE_URL}/api/revenuecat/unsubscribe`, {
        method: "POST",
        cache: "no-store",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "Cache-Control": "no-cache",
          Pragma: "no-cache",
        },
        body: JSON.stringify(payload),
      });

      const data = (await response.json().catch(() => ({}))) as CancelResponse;

      if (!response.ok || !data.success) {
        throw new Error(
          data?.message ||
            data?.error ||
            data?.revenuecat_error ||
            `Das Abo konnte nicht gekündigt werden: ${response.status}`,
        );
      }

      setCancelData(data);
      setStatusData((current) => ({
        ...(current || {}),
        success: true,
        has_subscription: true,
        customer_id: data.customer_id || current?.customer_id || null,
        subscription_id:
          data.subscription_id || current?.subscription_id || resolvedSubscriptionId,
        status: data.status || current?.status || "active",
        auto_renewal_status:
          data.auto_renewal_status ||
          current?.auto_renewal_status ||
          "will_not_renew",
        current_period_ends_at:
          data.current_period_ends_at || current?.current_period_ends_at || null,
        gives_access: true,
        message: data.message || current?.message,
      }));

      setSuccessText(
        data.message ||
          "Das Abo wurde gekündigt. Der Zugang bleibt bis zum Ende des bezahlten Zeitraums aktiv.",
      );

      notifyAccessChanged();
      onCancelled?.();
      onPortalOpened?.();
    } catch (error) {
      setErrorText(getReadableError(error));
    } finally {
      setCancelLoading(false);
    }
  };

  const handleClose = (): void => {
    resetMessages();
    setCancelData(null);
    onClose();
  };

  useEffect(() => {
    if (!isOpen) {
      setStatusLoading(false);
      setCancelLoading(false);
      setPortalLoading(false);
      setErrorText("");
      setSuccessText("");
      setStatusData(null);
      setCancelData(null);
      return;
    }

    void loadStatus();
  }, [isOpen, loadStatus]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleEscape = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        handleClose();
      }
    };

    window.addEventListener("keydown", handleEscape);

    return () => {
      window.removeEventListener("keydown", handleEscape);
    };
  });

  if (!isOpen) {
    return null;
  }

  const isBusy = statusLoading || cancelLoading || portalLoading;

  return (
    <div
      className="unsubscribe-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !isBusy) {
          handleClose();
        }
      }}
    >
      <section
        className="unsubscribe-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="unsubscribe-title"
      >
        <div className="unsubscribe-particles" aria-hidden="true">
          <span />
          <span />
          <span />
          <span />
        </div>

        <button
          type="button"
          className="unsubscribe-close"
          onClick={handleClose}
          aria-label="Schließen"
          disabled={isBusy}
        >
          <FaTimes />
        </button>

        <div className="unsubscribe-badge">
          <FaBan />
          <span>Abo verwalten</span>
        </div>

        <div className="unsubscribe-title-row">
          <div>
            <h1 id="unsubscribe-title">Premium-Abo verwalten</h1>

            <p>
              Hier können Sie den Status Ihres Website-Premium-Abos prüfen und
              die automatische Verlängerung kündigen.
            </p>
          </div>

          <div className="unsubscribe-icon">
            <FaShieldAlt />
          </div>
        </div>

        <div className="unsubscribe-info-grid">
          <div>
            <FaEnvelope />
            <span>E-Mail</span>
            <strong>{cleanEmail || "—"}</strong>
          </div>

          <div>
            <FaLock />
            <span>Abo</span>
            <strong>Website Premium</strong>
          </div>
        </div>

        <div className="unsubscribe-status-box">
          <div>
            <span>Status</span>
            <strong>{formatStatus(resolvedStatus)}</strong>
          </div>

          <div>
            <span>Verlängerung</span>
            <strong>{formatAutoRenewal(resolvedAutoRenewalStatus)}</strong>
          </div>

          <div>
            <span>Zugang bis</span>
            <strong>{formatTimestampDate(resolvedPeriodEnd)}</strong>
          </div>

          <div>
            <span>Subscription ID</span>
            <strong>{resolvedSubscriptionId || "—"}</strong>
          </div>

          <div>
            <span>Customer ID</span>
            <strong>{resolvedCustomerId || "—"}</strong>
          </div>
        </div>

        {statusLoading && (
          <div className="unsubscribe-info">
            <FaRedo className="unsubscribe-spin" />
            <span>Abo-Status wird geprüft...</span>
          </div>
        )}

        {successText && (
          <div className="unsubscribe-success">
            <FaCheckCircle />
            <span>{successText}</span>
          </div>
        )}

        {errorText && (
          <div className="unsubscribe-error">
            <FaExclamationCircle />
            <span>{errorText}</span>
          </div>
        )}

        {isAlreadyCancelled ? (
          <div className="unsubscribe-warning">
            <FaClock />
            <span>
              Dieses Abo ist bereits gekündigt. Der Zugang bleibt bis zum Ende
              des bezahlten Zeitraums aktiv.
            </span>
          </div>
        ) : (
          <div className="unsubscribe-warning">
            <FaExclamationCircle />
            <span>
              Nach der Kündigung bleibt der Zugang normalerweise bis zum Ende
              des bezahlten Zeitraums aktiv. Danach wird RevenueCat den Zugang
              automatisch entfernen.
            </span>
          </div>
        )}

        <div className="unsubscribe-actions">
          <button
            type="button"
            className="unsubscribe-primary"
            onClick={handleCancelSubscription}
            disabled={isBusy || !canRunActions || isAlreadyCancelled}
          >
            {cancelLoading ? (
              <>
                <FaRedo className="unsubscribe-spin" />
                <span>Abo wird gekündigt...</span>
              </>
            ) : isAlreadyCancelled ? (
              <>
                <FaCheckCircle />
                <span>Bereits gekündigt</span>
              </>
            ) : (
              <>
                <FaBan />
                <span>Automatische Verlängerung kündigen</span>
              </>
            )}
          </button>

          <button
            type="button"
            className="unsubscribe-secondary"
            onClick={() => void loadStatus()}
            disabled={isBusy || !canRunActions}
          >
            {statusLoading ? (
              <>
                <FaRedo className="unsubscribe-spin" />
                <span>Prüfung läuft...</span>
              </>
            ) : (
              <>
                <FaRedo />
                <span>Status prüfen</span>
              </>
            )}
          </button>

          <button
            type="button"
            className="unsubscribe-secondary"
            onClick={handleOpenPortal}
            disabled={isBusy || !canRunActions || !hasSubscription}
          >
            {portalLoading ? (
              <>
                <FaRedo className="unsubscribe-spin" />
                <span>Portal wird geöffnet...</span>
              </>
            ) : (
              <>
                <FaExternalLinkAlt />
                <span>Kundenportal</span>
              </>
            )}
          </button>

          <button
            type="button"
            className="unsubscribe-secondary"
            onClick={handleClose}
            disabled={isBusy}
          >
            Schließen
          </button>
        </div>
      </section>
    </div>
  );
};

export default UnSubscribe;